import { Readable } from "node:stream";

import { Connection, Pool } from "snowflake-sdk";

import { AbacusState, AbacusStateActionStatus } from "@abacus/ows-state-client";
import { AbacusEvent } from "@abacus/ows-event-client";
import { Environment, Logger, TypedReadable } from "@abacus/common";

import {
  GET_ABACUS_EVENTS,
  GET_ABACUS_STATES,
  GET_ACCOUNT_CONTRACTS_BY_ACCOUNT_IDS_AND_CONTRACT_IDS,
  GET_ACCOUNT_IDS_BY_ACCOUNT_IDS,
  GET_ACCOUNT_PAYMENT_ENTITY_IDS_BY_ACCOUNT_IDS,
  GET_ADJUSTMENT_TYPES,
  GET_CONTRACT_IDS_BY_CONTRACT_IDS,
  GET_CONTRACT_TERM_ATTACHMENTS_BY_TERM_TYPES_AND_CONTRACT_IDS,
  GET_CURRENT_STATEMENT_PERIOD_IDS_BY_STATEMENT_PERIOD_IDS,
  GET_STATEMENT_PERIODS_BY_YEARS,
  GET_UPCS_BY_DISPLAY_UPCS,
  GET_UPCS_BY_UPCS_AND_VENDOR_IDS,
} from "./queries";
import {
  AccountContract,
  AccountId,
  AccountPaymentEntityId,
  AdjustmentType,
  ContractId,
  ContractTerm,
  ContractTermType,
  SnowflakeClientOptions,
  StatementPeriod,
  StatementPeriodId,
  Upc,
  UpcVendor,
} from "./types";

export class SnowflakeClient {
  private db: string;
  private env: Environment;
  private logger?: Logger;
  private pool: Pool<Connection>;

  constructor({ database, environment, logger, pool }: SnowflakeClientOptions) {
    this.db = database;
    this.env = environment;
    this.logger = logger;
    this.pool = pool;
  }

  private get<T>(query: string): Promise<TypedReadable<T>> {
    this.logger?.debug(query);
    return this.pool.use(async (dbc) => {
      return dbc
        .execute({
          streamResult: true,
          sqlText: query,
        })
        .streamRows();
    });
  }

  async getAccountIdsByAccountIds(
    accountIds: number[],
  ): Promise<TypedReadable<AccountId>> {
    if (accountIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_ACCOUNT_IDS_BY_ACCOUNT_IDS(this.db, this.env, accountIds);
    return this.get(query);
  }

  async getAccountContractsByAccountIdsAndContractIds(
    accountIds: number[],
    contractIds: number[],
  ): Promise<TypedReadable<AccountContract>> {
    if (accountIds.length < 1) {
      return Readable.from([]);
    }
    if (contractIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_ACCOUNT_CONTRACTS_BY_ACCOUNT_IDS_AND_CONTRACT_IDS(
      this.db,
      this.env,
      accountIds,
      contractIds,
    );
    return this.get(query);
  }

  async getAccountPaymentEntityIdsByAccountIds(
    accountIds: number[],
  ): Promise<TypedReadable<AccountPaymentEntityId>> {
    if (accountIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_ACCOUNT_PAYMENT_ENTITY_IDS_BY_ACCOUNT_IDS(
      this.db,
      this.env,
      accountIds,
    );
    return this.get(query);
  }

  async getAbacusEvents(
    targetIds: number[],
    eventName?: string,
    targetType?: string,
    createdBy?: string,
  ): Promise<TypedReadable<AbacusEvent>> {
    if (targetIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_ABACUS_EVENTS(
      this.db,
      this.env,
      targetIds,
      eventName,
      targetType,
      createdBy,
    );
    return this.get(query);
  }

  async getAbacusStates(
    parentTableName: string,
    parentTableIds: number[],
    actionName?: string,
    actionStatus?: AbacusStateActionStatus,
  ): Promise<TypedReadable<AbacusState>> {
    if (parentTableIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_ABACUS_STATES(
      this.db,
      this.env,
      parentTableName,
      parentTableIds,
      actionName,
      actionStatus,
    );
    return this.get(query);
  }

  getAdjustmentTypes(): Promise<TypedReadable<AdjustmentType>> {
    const query = GET_ADJUSTMENT_TYPES(this.db, this.env);
    return this.get(query);
  }

  async getContractIdsByContractIds(
    contractIds: number[],
  ): Promise<TypedReadable<ContractId>> {
    if (contractIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_CONTRACT_IDS_BY_CONTRACT_IDS(this.db, this.env, contractIds);
    return this.get(query);
  }

  async getContractTermsByTermTypesAndContractIds(
    contractIds: number[],
    termTypes: ContractTermType[],
  ): Promise<TypedReadable<ContractTerm>> {
    if (contractIds.length < 1) {
      return Readable.from([]);
    }
    if (termTypes.length < 1) {
      return Readable.from([]);
    }
    const query = GET_CONTRACT_TERM_ATTACHMENTS_BY_TERM_TYPES_AND_CONTRACT_IDS(
      this.db,
      this.env,
      contractIds,
      termTypes,
    );
    return this.get(query);
  }

  async getCurrentStatementPeriodIdsByIds(
    statementPeriodIds: number[],
  ): Promise<TypedReadable<StatementPeriodId>> {
    if (statementPeriodIds.length < 1) {
      return Readable.from([]);
    }
    const query = GET_CURRENT_STATEMENT_PERIOD_IDS_BY_STATEMENT_PERIOD_IDS(
      this.db,
      this.env,
      statementPeriodIds,
    );
    return this.get(query);
  }

  async getStatementPeriodsByYears(
    years: number[],
  ): Promise<TypedReadable<StatementPeriod>> {
    if (years.length < 1) {
      return Readable.from([]);
    }
    const query = GET_STATEMENT_PERIODS_BY_YEARS(this.db, this.env, years);
    return this.get(query);
  }

  async getUpcsByDisplayUpcs(
    displayUpcs: string[],
  ): Promise<TypedReadable<Upc>> {
    if (displayUpcs.length < 1) {
      return Readable.from([]);
    }
    const query = GET_UPCS_BY_DISPLAY_UPCS(this.db, displayUpcs);
    return this.get(query);
  }

  async getUpcsByUpcsAndVendorIds(
    vendorIds: number[],
    upcs: string[],
  ): Promise<TypedReadable<UpcVendor>> {
    if (vendorIds.length < 1) {
      return Readable.from([]);
    }
    if (upcs.length < 1) {
      return Readable.from([]);
    }
    const query = GET_UPCS_BY_UPCS_AND_VENDOR_IDS(this.db, vendorIds, upcs);
    return this.get(query);
  }
}
